import { zodResolver } from '@hookform/resolvers/zod'
import { CurrencyDollarIcon } from '@phosphor-icons/react'
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useGetMarketShopLink } from '@/api/market-shop/settings/get-market-shop-link'
import { useGetMarketShopSettings } from '@/api/market-shop/settings/get-market-shop-settings'
import { useUpdateMarketShopSettings } from '@/api/market-shop/settings/update-market-shop-settings'
import { NumericInputFormField } from '@/components/form/numeric-input-form-field'
import { SwitchFormField } from '@/components/form/switch-form-field'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { Divider } from '@/components/ui/divider'
import { ErrorMessage } from '@/components/ui/error-message'
import { Tooltip } from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import { popupFactory, routerFactory } from '@/factories'
import { useNavigationGuard } from '@/hooks'
import { MARKET_SHOP_MAX_TIP_AMOUNT } from '@/models'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { selectUserRole } from '@/reducers/userDataReducer'
import { transformCentsToDollars, transformDollarsToCents } from '@/utils/currency'

import { BusinessHourRow } from './business-hour-row'
import { InventoryCategoriesSection } from './inventory-categories-section'
import * as S from './market-shop-settings.styled'
import {
  MarketShopSettingsFormData,
  makeMarketShopSettingsSchema,
} from './market-shop-settings.types'
import {
  apiBusinessHoursToForm,
  formBusinessHoursToApi,
  getDefaultBusinessHours,
  getDefaultTips,
} from './market-shop-settings.utils'

export const MarketShopSettingsPage = () => {
  const { t } = useTranslation(['market-shop-dashboard'])
  const activeBuilding = useSelector(selectActiveBuilding)
  const buildingId = activeBuilding?.id
  const { navigateBack } = routerFactory.useRouter()

  const { data: marketShopLink } = useGetMarketShopLink({ buildingId })
  const marketShopId = marketShopLink?.[0]?.id
  const { data, isLoading } = useGetMarketShopSettings({ marketShopId })
  const { mutateAsync: updateSettings, isPending } = useUpdateMarketShopSettings()

  // TODO: make better role handling with TS
  const userRole = useSelector(selectUserRole)
  const isAdmin = userRole === 'Administrator'

  const form = useForm<MarketShopSettingsFormData>({
    mode: 'onChange',
    resolver: zodResolver(makeMarketShopSettingsSchema(t)),
    // Note: Update form values when data is fetched from the API
    values: {
      businessHours: data?.businessHours
        ? apiBusinessHoursToForm(data.businessHours)
        : getDefaultBusinessHours(),
      categories: data?.categories ?? [],
      deliverToMe: data?.deliverToMe ?? false,
      displayNameForCashPayment: data?.displayNameForCashPayment ?? 'Cash',
      enablePayByCard: data?.enablePayByCard ?? true,
      enablePayByCash: data?.enablePayByCash ?? false,
      globalTax: (data?.globalTax ?? 0).toString() ?? '',
      tips:
        data?.tips?.length === 3
          ? data.tips.map((tip) => transformCentsToDollars(tip).toString())
          : getDefaultTips(),
      tipsEnabled: data?.tipsEnabled ?? false,
    },
  })

  const payByCashEnabled = useWatch({
    control: form.control,
    name: 'enablePayByCash',
  })

  const tipsEnabled = useWatch({
    control: form.control,
    name: 'tipsEnabled',
  })

  const { fields } = useFieldArray({
    control: form.control,
    name: 'businessHours',
  })

  const handleDayToggle = (index: number) => {
    const currentValue = form.getValues(`businessHours.${index}.enabled`)
    form.setValue(`businessHours.${index}.enabled`, !currentValue)
  }

  useNavigationGuard({ isBlocked: form.formState.isDirty && !isPending })

  const handleDiscardChanges = () =>
    navigateBack({ path: '/dashboard/building-dashboard/market-shop' })

  const onSubmit = async (formData: MarketShopSettingsFormData) => {
    if (!marketShopId) {
      return
    }

    const handleSave = async () => {
      const marketShopSettings = {
        categories: formData.categories.map((category) => ({
          // Note: If category is new - it has id equal to `new-category-${Date.now()}`
          // In such case - omit sending id to the API.
          ...{ id: typeof category.id === 'string' ? undefined : category.id },
          name: category.name,
          position: category.position,
        })),
        globalTax: Number(formData.globalTax),
        marketShopId,
      }

      const marketShopRestaurantSettings = {
        ...marketShopSettings,
        businessHours: formBusinessHoursToApi(formData.businessHours),
        deliverToMe: formData.deliverToMe,
        displayNameForCashPayment: formData.displayNameForCashPayment,
        enablePayByCash: formData.enablePayByCash,
        tips: formData.tips.map((tip) => transformDollarsToCents(tip)),
        tipsEnabled: formData.tipsEnabled,
        ...(isAdmin ? { enablePayByCard: formData.enablePayByCard } : {}),
      }

      await updateSettings(
        activeBuilding?.marketShopRestaurantAvailable
          ? marketShopRestaurantSettings
          : marketShopSettings,
      )
    }

    if (form.formState.dirtyFields.globalTax) {
      popupFactory.open(ConfirmationPopup, {
        cancelText: t('market-shop-dashboard:settings.globalTax.confirmation.cancelButton'),
        children: t('market-shop-dashboard:settings.globalTax.confirmation.message', {
          tax: formData.globalTax,
        }),
        confirmText: t('market-shop-dashboard:settings.globalTax.confirmation.confirmButton'),
        onConfirm: handleSave,
        title: t('market-shop-dashboard:settings.globalTax.confirmation.title'),
        variant: 'warning',
      })
      return
    }

    await handleSave()
  }

  return (
    <S.ScrollableCard>
      <FormProvider {...form}>
        <S.Container>
          {/* LEFT column: Tips, Deliver to Me, Working Hours */}
          {activeBuilding?.marketShopRestaurantAvailable && (
            <S.SettingsSection>
              <S.SettingRow>
                <S.SettingLabel>
                  <Typography size="text-medium" weight="medium">
                    {t('market-shop-dashboard:settings.tipsEnabled.label')}
                  </Typography>
                  <Tooltip>{t('market-shop-dashboard:settings.tipsEnabled.tooltip')}</Tooltip>
                </S.SettingLabel>
                <SwitchFormField name="tipsEnabled" disabled={isLoading || isPending} />
              </S.SettingRow>
              {tipsEnabled && (
                <S.TipInputsRow>
                  <NumericInputFormField
                    name="tips.0"
                    icon={CurrencyDollarIcon}
                    size="medium"
                    placeholder={t('market-shop-dashboard:settings.tips.placeholders.first')}
                    disabled={isLoading || isPending}
                    decimalPlaces={2}
                    mode="decimal"
                    max={MARKET_SHOP_MAX_TIP_AMOUNT}
                  />
                  <NumericInputFormField
                    name="tips.1"
                    icon={CurrencyDollarIcon}
                    size="medium"
                    placeholder={t('market-shop-dashboard:settings.tips.placeholders.second')}
                    disabled={isLoading || isPending}
                    decimalPlaces={2}
                    mode="decimal"
                    max={MARKET_SHOP_MAX_TIP_AMOUNT}
                  />
                  <NumericInputFormField
                    name="tips.2"
                    icon={CurrencyDollarIcon}
                    size="medium"
                    placeholder={t('market-shop-dashboard:settings.tips.placeholders.third')}
                    disabled={isLoading || isPending}
                    decimalPlaces={2}
                    mode="decimal"
                    max={MARKET_SHOP_MAX_TIP_AMOUNT}
                  />
                </S.TipInputsRow>
              )}

              <Divider />

              <S.SettingRow>
                <S.SettingLabel>
                  <Typography size="text-medium" weight="medium">
                    {t('market-shop-dashboard:settings.deliverToMe.label')}
                  </Typography>
                  <Tooltip>{t('market-shop-dashboard:settings.deliverToMe.tooltip')}</Tooltip>
                </S.SettingLabel>
                <SwitchFormField name="deliverToMe" disabled={isLoading || isPending} />
              </S.SettingRow>

              <S.SectionTitle>
                <Typography size="text-medium" weight="medium">
                  {t('market-shop-dashboard:settings.workingHours.title')}
                </Typography>
                <Tooltip>{t('market-shop-dashboard:settings.workingHours.tooltip')}</Tooltip>
              </S.SectionTitle>
              <Alert type="info" message={t('market-shop-dashboard:settings.workingHours.alert')} />
              <S.DaysList>
                {fields.map((field, index) => (
                  <BusinessHourRow
                    key={field.id}
                    disabled={isLoading || isPending}
                    index={index}
                    onToggle={() => handleDayToggle(index)}
                  />
                ))}
              </S.DaysList>
            </S.SettingsSection>
          )}

          {/* RIGHT column: Pay by cash, Global tax, Inventory Categories */}
          <S.SettingsSection>
            {isAdmin && activeBuilding?.marketShopRestaurantAvailable && (
              <>
                <S.SettingRow>
                  <S.SettingLabel>
                    <Typography size="text-medium" weight="medium">
                      {t('market-shop-dashboard:settings.enablePayByCard.label')}
                    </Typography>
                    <Tooltip>{t('market-shop-dashboard:settings.enablePayByCard.tooltip')}</Tooltip>
                  </S.SettingLabel>
                  <SwitchFormField name="enablePayByCard" disabled={isLoading || isPending} />
                </S.SettingRow>
                {/* TODO: it should not work like that, validation will be enhanced asap */}
                {form.formState.errors.enablePayByCard && (
                  <div style={{ paddingBottom: '1.6rem' }}>
                    <ErrorMessage>{form.formState.errors.enablePayByCard.message}</ErrorMessage>
                  </div>
                )}
                <Divider />
              </>
            )}
            {activeBuilding?.marketShopRestaurantAvailable && (
              <>
                <S.SettingRow>
                  <S.SettingLabel>
                    <Typography size="text-medium" weight="medium">
                      {t('market-shop-dashboard:settings.enablePayByCash.label')}
                    </Typography>
                    <Tooltip>{t('market-shop-dashboard:settings.enablePayByCash.tooltip')}</Tooltip>
                  </S.SettingLabel>
                  <SwitchFormField name="enablePayByCash" disabled={isLoading || isPending} />
                </S.SettingRow>
                {payByCashEnabled && (
                  <S.CashInputRow>
                    <TextInputFormField
                      name="displayNameForCashPayment"
                      label={t('market-shop-dashboard:settings.displayNameForCashPayment.label')}
                      disabled={isLoading || isPending}
                    />
                  </S.CashInputRow>
                )}

                <Divider />
              </>
            )}

            <S.GlobalTaxContainer>
              <NumericInputFormField
                name="globalTax"
                size="medium"
                placeholder={t('market-shop-dashboard:settings.globalTax.placeholder')}
                disabled={isLoading || isPending}
                label={t('market-shop-dashboard:settings.globalTax.label')}
                mode="integer"
                min={0}
                max={100}
                helperText={t('market-shop-dashboard:settings.globalTax.helperText')}
              />
            </S.GlobalTaxContainer>

            <Divider />

            <InventoryCategoriesSection />
          </S.SettingsSection>
        </S.Container>
        <S.Footer>
          <Button use="secondary" disabled={isPending} onClick={handleDiscardChanges}>
            {t('market-shop-dashboard:settings.discardChanges')}
          </Button>
          <Button loading={isPending} disabled={isLoading} onClick={form.handleSubmit(onSubmit)}>
            {t('market-shop-dashboard:settings.saveChanges')}
          </Button>
        </S.Footer>
      </FormProvider>
    </S.ScrollableCard>
  )
}
